import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Minus, Plus, Trash2, Type } from "lucide-react";
import { LineTable } from "./LineTable";
import { formatMoney } from "../../lib/money";
import { sectionCode } from "../../lib/sectionCode";
import type { DraftSection } from "./useQuoteDraft";
import type { Money, OutsourceStatus, QuoteLine, QuoteSection, SectionMode } from "../../api/types";
import { OUTSOURCE_STATUS_LABEL } from "../../api/types";

// 一個章節。
//
// DETAILED 逐項計價;LUMP_SUM(例如公共責任險)**也有明細**,只是不逐一標價 ——
// 業主 2026-09-17:「整筆包價的章節沒有明細 這是錯誤的 / 我們還是需要細節
// 只是差在 沒有逐一給項目單價」。
//
// 客戶要看得懂那筆錢買到了什麼:三行「公共意外責任險 500萬/1000萬」、
// 「活動期間人員保險」、「保單製作與送審」,比一行「公共責任險 35,000」有用得多。

const MODE_OPTIONS: Array<{ value: SectionMode; label: string }> = [
  { value: "DETAILED", label: "逐項明細" },
  { value: "LUMP_SUM", label: "整筆包價" },
];

// 發包狀態要改就在這裡改;側邊欄的 tag 只讀,改完隨報價單一起存檔。
const OUTSOURCE_OPTIONS: OutsourceStatus[] = ["NOT_ORDERED", "ORDERED", "PENDING"];

const LAYOUT_OPTIONS = [
  { value: "STANDARD", label: "標準(數量 × 單價)" },
  { value: "MANPOWER", label: "人力(人數 × 天數 × 單價)" },
] as const;

const SMALL_FIELD =
  "h-9 min-w-0 rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)] " +
  "px-2 text-sm text-[var(--text)]";

// 摺疊狀態是**檢視偏好,不是文件內容**。寫進草稿的話,摺一下就會讓報價單變成
// 「有未存檔的修改」,存檔後還會多出一個看不出任何差別的版本 —— 那正是
// revision_service 刻意避免的雜訊。所以存在瀏覽器,而且每個 uid 各自記。
//
// localStorage 在隱私模式或封鎖網站資料時讀寫都會丟例外,一律包起來:
// 記不住只是下次重新展開,不該讓整個編輯器掛掉。
const COLLAPSE_KEY = "nq.section.collapsed";

function readCollapsed(): Record<string, boolean> {
  try {
    return JSON.parse(window.localStorage.getItem(COLLAPSE_KEY) || "{}") ?? {};
  } catch {
    return {};
  }
}

function useSectionCollapsed(uid: string): [boolean, (next: boolean) => void] {
  const [collapsed, setState] = useState(false);

  // 初值在 effect 裡讀,不在 useState 的初始化裡 —— 那樣每個章節都會在
  // 每次 render 解一次 JSON,而且伺服器端渲染時根本沒有 window。
  useEffect(() => {
    setState(Boolean(readCollapsed()[uid]));
  }, [uid]);

  const set = useCallback(
    (next: boolean) => {
      setState(next);
      try {
        const all = readCollapsed();
        if (next) all[uid] = true;
        else delete all[uid];
        window.localStorage.setItem(COLLAPSE_KEY, JSON.stringify(all));
      } catch {
        /* 記不住就算了,畫面照常運作 */
      }
    },
    [uid],
  );

  return [collapsed, set];
}

export interface SectionCardProps {
  section: DraftSection;
  index: number;
  sectionCount: number;
  subtotal: Money;
  onChangeSection: (patch: Partial<Omit<QuoteSection, "lines">>) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
  onAddLine: (patch?: Partial<QuoteLine>) => void;
  onChangeLine: (lineIndex: number, patch: Partial<QuoteLine>) => void;
  onMoveLine: (from: number, to: number) => void;
  onRemoveLine: (lineIndex: number) => void;
  /** 焦點進到這個章節。⌘K 從品項庫帶入時要知道游標停在哪一章。 */
  onActivate?: () => void;
}

export function SectionCard({
  section,
  index,
  sectionCount,
  subtotal,
  onChangeSection,
  onMoveUp,
  onMoveDown,
  onRemove,
  onAddLine,
  onChangeLine,
  onMoveLine,
  onRemoveLine,
  onActivate,
}: SectionCardProps) {
  const title = section.title || "(未命名章節)";
  const lumpSumId = `nq-lump-${section.uid}`;
  const bodyId = `nq-section-body-${section.uid}`;
  // 序號看位置,不看後端傳來的 code:新增或搬動分類時畫面要當場就對,
  // 不必等存檔繞一趟後端。後端存檔時會寫進同一個值(app/numbering.py)。
  const code = sectionCode(index);
  const [collapsed, setCollapsed] = useSectionCollapsed(section.uid);
  // 舊資料沒有這一欄時當成未發包,下拉才不會出現空白選項。
  const outsourceStatus: OutsourceStatus = section.outsource_status ?? "NOT_ORDERED";
  // 刪到一列都不剩時,焦點交給這顆按鈕 —— 那是這個章節唯一還在的入口。
  const addLineRef = useRef<HTMLButtonElement | null>(null);
  // 標題列要不要印小計:摺疊時明細看不到,整筆包價根本沒有明細表。
  // 收合時才把小計挪到標題列 —— 整筆包價現在也有明細表,
  // 小計就跟其他章節一樣留在明細的最下面,兩處各有一份反而對不起來。
  const showHeaderSubtotal = collapsed;

  // 「在第 N 列下方插入一列」。草稿的原語只有「附加到最後」與「搬移」,
  // 這裡把兩者接起來就夠了 —— 不必為了插入去動 useQuoteDraft 的狀態機。
  // 兩個更新都是函式式的,React 會依序套用在最新的狀態上:
  // 附加後新列位於索引 section.lines.length,再搬到 afterIndex + 1。
  const insertAfter = (afterIndex: number, patch: Partial<QuoteLine>) => {
    onAddLine(patch);
    onMoveLine(section.lines.length, afterIndex + 1);
  };

  // 複製此列。id 一定要清掉:沿用原列的 id 送給後端會被當成「同一列」,
  // 兩列互相覆蓋,最後只剩一列。uid 由草稿自己重新配,不必在這裡處理。
  const duplicateLine = (lineIndex: number) => {
    const source = section.lines[lineIndex];
    if (!source) return;
    const { uid: _uid, ...rest } = source;
    insertAfter(lineIndex, { ...rest, id: null });
  };

  return (
    <section
      id={`nq-section-${section.uid}`}
      aria-label={`章節:${title}`}
      // 用 capture:焦點落在章節裡的任何一格,都算游標停在這一章。
      onFocusCapture={onActivate}
      className="rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] shadow-card"
    >
      <div className="flex flex-wrap items-center gap-2 border-b border-[var(--border)] px-3 py-2.5">
        {/* 序號原本是可填的輸入框,業主 2026-09-11 說那是多餘的 —— 現在由位置推導,
            原地換成摺疊鈕。序號本身唯讀顯示在鈕的右邊。 */}
        <button
          type="button"
          onClick={() => setCollapsed(!collapsed)}
          aria-expanded={!collapsed}
          aria-controls={bodyId}
          aria-label={collapsed ? `展開「${title}」` : `摺疊「${title}」`}
          title={collapsed ? "展開這個分類" : "摺疊這個分類"}
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)] text-[var(--text-2)] hover:bg-[var(--surface-2)]"
        >
          {collapsed ? <Plus aria-hidden="true" size={16} /> : <Minus aria-hidden="true" size={16} />}
        </button>
        <span className="tabular w-6 shrink-0 text-center text-sm font-medium text-[var(--text-3)]">
          {code}
        </span>
        <input
          type="text"
          value={section.title}
          aria-label="章節標題"
          placeholder="章節名稱"
          onChange={(event) => onChangeSection({ title: event.target.value })}
          className={`${SMALL_FIELD} w-0 min-w-[10rem] flex-1`}
        />
        {/* 三個下拉一律固定寬度(業主 2026-09-17:「不用有多字 就會被擠過去
            跑板的問題產生」)。寬度跟著選到的字走的話,換一個選項整排就位移,
            兩張卡片並排時右邊那三顆鈕不在同一條線上。 */}
        <select
          value={section.mode}
          aria-label="章節模式"
          onChange={(event) => onChangeSection({ mode: event.target.value as SectionMode })}
          className={`${SMALL_FIELD} w-28 shrink-0`}
        >
          {MODE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {/* **兩種計價方式都畫這一顆。**

            原本只有逐項明細才畫,因為整筆包價沒有明細表 —— 而整筆包價現在
            也有明細了(業主 2026-09-17),標準型與人力型對它一樣有意義。

            順帶解掉半個跑版:少畫一顆,整排就往左位移一格。 */}
        <select
          value={section.line_layout}
          aria-label="明細版面"
          onChange={(event) =>
            onChangeSection({ line_layout: event.target.value as (typeof LAYOUT_OPTIONS)[number]["value"] })
          }
          className={`${SMALL_FIELD} w-44 shrink-0`}
        >
          {LAYOUT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {/* 發包狀態在這裡改,側邊欄的 tag 跟著變;改完隨報價單一起存檔。
            下拉本身就寫著「未發包 / 已發包 / 待確認」,旁邊不必再放一個同字的徽章。 */}
        <select
          value={outsourceStatus}
          aria-label="發包狀態"
          onChange={(event) => onChangeSection({ outsource_status: event.target.value as OutsourceStatus })}
          className={`${SMALL_FIELD} w-24 shrink-0`}
        >
          {OUTSOURCE_OPTIONS.map((value) => (
            <option key={value} value={value}>
              {OUTSOURCE_STATUS_LABEL[value]}
            </option>
          ))}
        </select>

        {/* 小計平常印在明細的最下面(見 LineTable 的 tfoot),標題列不再重複一份。
            但有兩種情形非留在這裡不可:

            1. **摺疊起來的時候。** 明細連同 tfoot 一起收掉,那一類多少錢就再也讀不到,
               那是退步 —— 摺疊時把小計補回標題列。
            2. **整筆包價的章節。** 它根本沒有明細表,金額就是下面那一格輸入;
               在輸入框下面再補一列小計只是把同一個數字寫兩次。 */}
        {showHeaderSubtotal ? (
          <p
            data-testid="section-subtotal"
            className="m-0 ml-auto flex shrink-0 items-baseline gap-1.5 text-sm text-[var(--text-2)]"
          >
            {/* **「小計」兩個字與數字各自固定位置。**
                「66,100」比「0」寬,寬度跟著數字走的話後面那三顆鈕會被推走 ——
                兩張卡片並排時就對不齊,而那正是業主看到的「跑板」。

                只把整塊固定寬度還不夠:整塊靠右對齊的話,數字短的那一列
                「小計」兩個字會往右跑 —— 鈕對齊了、字還是沒對齊。
                所以標籤不縮,數字自己一格固定寬度、靠右。 */}
            <span className="shrink-0">小計</span>
            <span
              data-testid="section-subtotal-amount"
              className="tabular w-24 shrink-0 text-right text-base font-semibold text-[var(--text)]"
            >
              {formatMoney(subtotal)}
            </span>
          </p>
        ) : null}

        <div className={`flex shrink-0 items-center gap-1${showHeaderSubtotal ? "" : " ml-auto"}`}>
          <button
            type="button"
            onClick={onMoveUp}
            disabled={index === 0}
            aria-label={`「${title}」上移`}
            className="rounded-[var(--radius)] p-1.5 text-[var(--text-3)] hover:bg-[var(--surface-2)] hover:text-[var(--text)] disabled:opacity-40"
          >
            <ChevronUp aria-hidden="true" size={16} />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={index === sectionCount - 1}
            aria-label={`「${title}」下移`}
            className="rounded-[var(--radius)] p-1.5 text-[var(--text-3)] hover:bg-[var(--surface-2)] hover:text-[var(--text)] disabled:opacity-40"
          >
            <ChevronDown aria-hidden="true" size={16} />
          </button>
          <button
            type="button"
            onClick={onRemove}
            aria-label={`刪除「${title}」`}
            className="rounded-[var(--radius)] p-1.5 text-[var(--text-3)] hover:bg-[var(--surface-2)] hover:text-[var(--danger)]"
          >
            <Trash2 aria-hidden="true" size={16} />
          </button>
        </div>
      </div>

      {collapsed ? null : (
        <div id={bodyId}>
          {/* 整筆包價的金額擺在明細表**上面** —— 它是這個章節的價錢,
              先看到金額再看買到了什麼,和封面的讀法一致。 */}
          {section.mode === "LUMP_SUM" ? (
            <div className="flex flex-wrap items-end gap-3 border-b border-[var(--border)] px-3 py-3">
              <div className="flex min-w-0 flex-col gap-1">
                <label htmlFor={lumpSumId} className="text-sm font-medium text-[var(--text-2)]">
                  包價金額
                </label>
                <input
                  id={lumpSumId}
                  type="text"
                  inputMode="decimal"
                  value={section.lump_sum_amount ?? ""}
                  onChange={(event) => onChangeSection({ lump_sum_amount: event.target.value })}
                  className={`${SMALL_FIELD} tabular w-40 text-right`}
                />
              </div>
              <p className="m-0 text-sm text-[var(--text-3)]">
                底下的明細只說明買到了什麼,<strong>不逐一標價</strong>;
                這個章節的金額以上面這一格為準。
              </p>
            </div>
          ) : null}
          <LineTable
            section={section}
            subtotal={subtotal}
            onChangeLine={onChangeLine}
            onMoveLine={onMoveLine}
            onRemoveLine={onRemoveLine}
            onInsertLine={insertAfter}
            onDuplicateLine={duplicateLine}
            emptyFocusRef={addLineRef}
          />
          <div className="flex flex-wrap gap-2 border-t border-[var(--border)] px-3 py-2">
            <button
              ref={addLineRef}
              type="button"
              onClick={() => onAddLine()}
              aria-label={`在「${title}」新增一行`}
              className="inline-flex items-center gap-1.5 rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)] px-2.5 py-1 text-sm text-[var(--text)] hover:bg-[var(--surface-2)]"
            >
              <Plus aria-hidden="true" size={14} />
              新增一行
            </button>
            <button
              type="button"
              onClick={() => onAddLine({ kind: "SUBHEADING", qty: null, unit_price: "0" })}
              aria-label={`在「${title}」插入標題列`}
              className="inline-flex items-center gap-1.5 rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)] px-2.5 py-1 text-sm text-[var(--text)] hover:bg-[var(--surface-2)]"
            >
              <Type aria-hidden="true" size={14} />
              插入標題列
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
